import {createInjectionState, useSessionStorage} from '@vueuse/core'
import {AtendimentoRepository, AtendimentoModel, AxiosHttpClient, Conteudo, Inscrito} from "@/modern/core"
import {useMutation, useQueryCache} from '@pinia/colada'
import {useStringUtils} from "@/modern/composables/useStringUtils";
import {useRoute} from "vue-router";
import { merge } from 'lodash-es';
import {useEmitter} from "@bussola-social/ui";
import {shortenFileName} from "@/modern/core/utils/string";
const uiEvent = useEmitter();

function parseInscrito(inscrito: Inscrito): Partial<Inscrito> {
    const justificativa = {
        tipo_falta_id: inscrito.justificativa?.motivo_id || null,
        outro_motivo: inscrito.justificativa?.motivo_outro || '',
        abonar: inscrito.justificativa?.abono || null,
        arquivo: inscrito.justificativa?.anexo && inscrito.justificativa?.anexo.length > 0 ? inscrito.justificativa?.anexo[0].path : null,
        excluir_arquivo: false,
        excluir_justificativa: false
    }

    return {
        ...inscrito,
        ...justificativa,
        status: inscrito.status ? inscrito.status : 'AUS'
    }
}

const [useProvideRegistroAtendidoStore, useRegistroAtendidoStore] = createInjectionState(() => {
    const queryCache = useQueryCache()
    const {minutesToMask, minutesFromMask} = useStringUtils()
    const {params} = useRoute()
    const state = useSessionStorage(
        `registro-atendimento-${params.id}`,
        {} as Partial<AtendimentoModel>
    )

    const initialize = (initialValue: AtendimentoModel) => {
        state.value = merge({
            //@ts-expect-error
            tipo_atendimento_id: initialValue?.tipo_atendimento?.id || null,
            relato: initialValue?.relato || '',
            observacoes: initialValue?.observacoes || '',
            total_participantes: Number(initialValue?.total_participantes) || 0,
            conteudosId: initialValue?.conteudos.filter(c => c.trabalhado).map(c => c.id.toString()) || [],
            inscritos: initialValue?.inscritos.map(i => parseInscrito(i)) || [],
            conteudos: initialValue?.conteudos.filter(c => c.trabalhado).map(i => ({
                ...i,
                carga_horaria_encontro: minutesToMask(i.carga_horaria_encontro || 0),
            })) as Conteudo[],
            anexos: initialValue?.anexos || [],
        }, state.value) as Partial<AtendimentoModel>
    }

    const uploadFile = async (file: File) => {
        const repository = AtendimentoRepository.instance(AxiosHttpClient.getInstance())
        const res = await repository.uploadAnexo(file, params.id as string)
        res.fold(
            ()=> {
                uiEvent.emit('add-snack', {
                    text: 'Falha ao enviar o arquivo, tente novamente',
                    color: 'error'
                });
            },
            (data) => {
                if(!state.value.anexos) {
                   state.value.anexos = []
                }
                const file_name = data.path.split('/').pop() || ''
                state.value.anexos.push({
                    ...data,
                    name: shortenFileName(file_name, 20),
                })
            }
        )
    }

    const {
        mutateAsync: save,
        status,
        asyncStatus,
        isLoading,
        error
    } = useMutation({
        mutation: async (id: number | string) => {
            const data = {
                ...state.value,
                conteudos: state.value.conteudos?.map(c => ({
                    id: c.id,
                    carga_horaria: minutesFromMask(c.carga_horaria_encontro || ''),
                })) || [],
                anexos: state.value.anexos?.map(a => a.path) || [],
            }
            const repository = AtendimentoRepository.instance(AxiosHttpClient.getInstance())
            return repository.saveAtendimento(
                data,
                id
            )
        },
        onSuccess: async () => {
            await queryCache.invalidateQueries(
                {},
                'all'
            )
        }
    })

    return {
        mutate: state,
        save,
        error,
        status,
        asyncStatus,
        isSaving: isLoading,
        initialize,
        uploadFile,
    }
})

export {useProvideRegistroAtendidoStore, useRegistroAtendidoStore}
